import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { GS1LayoutModule, GS1ButtonDirective, GS1FormModule } from '@gs1france/gs1-components-library';
import { Subscription } from 'rxjs';
import { InfoPerso } from '../../models/info-perso.model';
import { CustomerServiceService } from '@services/customerService.service';
import { StorageService } from '@services/storage.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { take, tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'unsubscribe',
  templateUrl: './unsubscribe.component.html',
  styleUrls: ['./unsubscribe.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [FormsModule, ReactiveFormsModule, MatDialogModule, MatStepperModule, MatTooltipModule, RouterLink, TranslateModule, GS1LayoutModule, GS1FormModule, GS1ButtonDirective]
})
export class UnsubscribeComponent implements OnInit, OnDestroy {
  private storageService = inject(StorageService);
  private dialog = inject(MatDialog);
  private fb = inject(UntypedFormBuilder);
  private customerService = inject(CustomerServiceService);

  subs: Subscription[] = [];
  isEnabled = false;
  unsubscribeForm: UntypedFormGroup;
  dialogRef: MatDialogRef<any>;

  company = this.storageService.selectedCompany;
  currentUser = toSignal(this.storageService.getUser(), { requireSync: false });

  @ViewChild('dialog') dialogContent: TemplateRef<any>;
  @ViewChild('phoneNumber') phoneNumberField;

  ngOnInit(): void {
    this.isEnabled = this.storageService.isUserAdmin();
  }

  constructForm(user: InfoPerso) {
    this.unsubscribeForm = this.fb.group({
      acceptUnsubscribe: ['', Validators.required],
      reason: ['', Validators.required],
      firstName: [user.prenom, Validators.compose([Validators.required, Validators.minLength(2), Validators.pattern(/[A-Za-zà-ù\-\s]+/)])],
      lastName: [user.nom, Validators.compose([Validators.required, Validators.minLength(2), Validators.pattern(/[A-Za-zà-ù\-\s]+/)])],
      phoneNumber: [user.telPrincipal, Validators.required]
    });

    this.subs.push(
      this.unsubscribeForm.get('reason').valueChanges.subscribe((value: string) => {
        this.updateCommentValidators(value);
      })
    );
  }

  updatePhoneField(value: string) {
    this.unsubscribeForm.get('phoneNumber').markAsDirty();
    this.unsubscribeForm.get('phoneNumber').markAsTouched();
    this.phoneNumberField.writeValue(value);
    this.phoneNumberField.onPhoneNumberChange();
    this.unsubscribeForm.get('phoneNumber').setValue(this.phoneNumberField.numberInstance.number);
    this.unsubscribeForm.get('phoneNumber').updateValueAndValidity();
  }

  updateCommentValidators(value: string) {
    if (value === 'autre') {
      this.unsubscribeForm.get('comment').clearValidators();
      this.unsubscribeForm.get('comment').setValidators(Validators.compose([Validators.required, Validators.minLength(5), Validators.maxLength(200)]));
    } else {
      this.unsubscribeForm.get('comment').clearValidators();
      this.unsubscribeForm.get('comment').setValidators(Validators.compose([Validators.minLength(5), Validators.maxLength(200)]));
    }
    this.unsubscribeForm.get('comment').updateValueAndValidity();
    this.unsubscribeForm.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  showUnsubscribePopin(): void {
    const user = this.currentUser();
    if (user) {
      this.constructForm(user);
    }
    const dialogOption: MatDialogConfig = new MatDialogConfig();
    dialogOption.width = '670px';
    dialogOption.maxWidth = '95vw';
    dialogOption.autoFocus = false;
    dialogOption.panelClass = 'unsubscribe-dialog';
    dialogOption.disableClose = false;
    this.dialogRef = this.dialog.open(this.dialogContent, dialogOption);
  }

  closeUnsubscribePopin(): void {
    this.dialog.closeAll();
  }

  askUnsubscribe() {
    if (this.unsubscribeForm.valid) {
      const form = new FormData();
      const data = this.unsubscribeForm.value;
      for (const key in data) {
        form.append(key, data[key]);
      }
      this.customerService
        .unsubscribe(data)
        .pipe(
          take(1),
          tap(() => this.dialogRef.close()),
          catchError(() => {
            this.dialogRef.close();
            return of(null);
          })
        )
        .subscribe();
    }
  }

  close() {
    this.dialogRef.close();
  }

  stepChanged(step: any) {
    if (step.selectedIndex === 2) {
      const user = this.currentUser();
      if (user) {
        this.updatePhoneField(user.telPrincipal);
      }
    }
  }
}
